import { useTranslation } from "react-i18next"

import type {
  SkillPackageBudgets,
  SkillPackageFormErrorCode,
  SkillPackageFormState,
  SkillPackageFormValidation,
} from "../utils/skill-package-draft"

import { SKILL_PACKAGE_REFERENCE_SUFFIXES } from "@/api/admin/skill-packages"
import { Plus, Trash2 } from "@/components/icons"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"

import { SkillPackageBudgetCounter, SkillPackageFormError } from "./skill-package-editor-feedback"
import { createBlankSkillPackageFile } from "../utils/skill-package-draft"
import { SKILL_PACKAGE_FORM_ERROR_DEFAULTS } from "../utils/skill-package-editor-constants"

// The "References" tab (#3074): named text files the skill reads on demand.
//
// Scripts are shown here as a COUNT, never as an editor. A zip-imported
// package can carry `scripts/` that runs inside the agent's container, and the
// portal edits prose, not executable code — but an admin opening such a
// package must be told the scripts are still there, or a silent round trip
// looks like the editor ate half the package.

export function SkillPackageReferencesTab({
  form,
  patch,
  budgets,
  validation,
  showErrors,
}: {
  form: SkillPackageFormState
  patch: (partial: Partial<SkillPackageFormState>) => void
  budgets: SkillPackageBudgets
  validation: SkillPackageFormValidation
  showErrors: boolean
}) {
  const { t } = useTranslation()
  const scriptCount = Object.keys(form.scripts).length

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.skillPackages.editor.referencesHelp", {
          defaultValue:
            "Named text files the agent can read on demand — templates, checklists, tables. Allowed types: {{suffixes}}.",
          suffixes: SKILL_PACKAGE_REFERENCE_SUFFIXES.join(", "),
        })}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SkillPackageBudgetCounter budget={budgets.files} unit="files" />
        <SkillPackageBudgetCounter budget={budgets.total} unit="bytes" />
      </div>

      {scriptCount > 0 ? (
        <Alert>
          <AlertDescription>
            {t("adminDashboard.skillPackages.editor.scriptsPreserved", {
              defaultValue:
                "This package carries {{count}} script file(s) from its zip. They are kept exactly as they are — the portal edits prose, not executable code.",
              count: scriptCount,
            })}
          </AlertDescription>
        </Alert>
      ) : null}

      <ul className="space-y-3">
        {form.references.map((file, index) => (
          <li key={file.key} className="space-y-2 rounded-lg border p-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-48 flex-1 space-y-2">
                <Label htmlFor={`skill-reference-path-${file.key}`}>
                  {t("adminDashboard.skillPackages.editor.referencePath", {
                    defaultValue: "File name",
                  })}
                </Label>
                <Input
                  className="font-mono"
                  data-testid={`skill-reference-path-${index}`}
                  id={`skill-reference-path-${file.key}`}
                  placeholder="checklist.md"
                  value={file.path}
                  onChange={(event) =>
                    patch({
                      references: form.references.map((entry) =>
                        entry.key === file.key ? { ...entry, path: event.target.value } : entry
                      ),
                    })
                  }
                />
              </div>
              <Button
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.skillPackages.editor.removeReference", {
                  defaultValue: "Remove reference file",
                })}
                onClick={() =>
                  patch({ references: form.references.filter((entry) => entry.key !== file.key) })
                }
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
            <Textarea
              className="font-mono text-xs"
              data-testid={`skill-reference-content-${index}`}
              rows={5}
              value={file.content}
              onChange={(event) =>
                patch({
                  references: form.references.map((entry) =>
                    entry.key === file.key ? { ...entry, content: event.target.value } : entry
                  ),
                })
              }
            />
            <ReferenceError code={showErrors ? validation.references[file.key] : undefined} />
          </li>
        ))}
      </ul>

      <Button
        data-testid="skill-package-add-reference"
        disabled={budgets.files.used >= budgets.files.max}
        size="sm"
        type="button"
        variant="outline"
        onClick={() => patch({ references: [...form.references, createBlankSkillPackageFile()] })}
      >
        <Plus className="size-4" />
        {t("adminDashboard.skillPackages.editor.addReference", {
          defaultValue: "Add reference file",
        })}
      </Button>
      <SkillPackageFormError
        codes={validation.form}
        match={["too_many_files", "total_too_large"]}
        show={showErrors}
      />
    </div>
  )
}

function ReferenceError({ code }: { code: SkillPackageFormErrorCode | undefined }) {
  const { t } = useTranslation()
  if (!code) return null
  return (
    <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
      {t(`adminDashboard.skillPackages.editor.errors.${code}`, {
        defaultValue: SKILL_PACKAGE_FORM_ERROR_DEFAULTS[code],
      })}
    </p>
  )
}
